import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './GameStyles.css';

// Утилита для touch на Android WebView
const useTouchClick = (handler: () => void, disabled?: boolean) => {
  const touchedRef = useRef(false);
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    touchedRef.current = true;
    handler();
    setTimeout(() => { touchedRef.current = false; }, 300);
  }, [disabled, handler]);
  const onClick = useCallback(() => {
    if (disabled || touchedRef.current) { touchedRef.current = false; return; }
    handler();
  }, [disabled, handler]);
  return { onTouchEnd, onClick };
};

interface AsteroidConfig {
  value: number;
  type: string;
  emoji?: string;
  jackpot: number;
  good: number;
  small: number;
  empty: number;
  duration: number;
  boost_percentage: number;
}

interface AsteroidMinerProps {
  onPlay: (choice?: any) => void;
  onClose: () => void;
  cost: number;
  maxReward: number;
  minReward: number;
  error?: string;
  isPlaying?: boolean;
  config?: AsteroidConfig[];
  activeBoost?: {
    multiplier: number;
    timeLeft: number;
    expiresAt: string;
  } | null;
}

const AsteroidMiner: React.FC<AsteroidMinerProps> = ({
  onPlay,
  onClose,
  cost,
  maxReward,
  minReward,
  error,
  isPlaying,
  config,
  activeBoost
}) => {
  const { t } = useTranslation();
  const [selectedAsteroid, setSelectedAsteroid] = useState<number | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  // Дефолтный конфиг с типами
  const defaultConfig: AsteroidConfig[] = [
    { value: 1, type: 'rock', emoji: '🪨', jackpot: 0.02, good: 0.10, small: 0.20, empty: 0.68, duration: 60, boost_percentage: 100 },
    { value: 2, type: 'ice', emoji: '❄️', jackpot: 0.03, good: 0.12, small: 0.20, empty: 0.65, duration: 90, boost_percentage: 100 },
    { value: 3, type: 'gold', emoji: '🥇', jackpot: 0.05, good: 0.15, small: 0.20, empty: 0.60, duration: 90, boost_percentage: 150 },
    { value: 4, type: 'rock', emoji: '🪨', jackpot: 0.02, good: 0.10, small: 0.20, empty: 0.68, duration: 60, boost_percentage: 100 },
    { value: 5, type: 'rare', emoji: '💎', jackpot: 0.08, good: 0.18, small: 0.22, empty: 0.52, duration: 120, boost_percentage: 200 },
    { value: 6, type: 'ice', emoji: '❄️', jackpot: 0.03, good: 0.12, small: 0.20, empty: 0.65, duration: 90, boost_percentage: 100 },
    { value: 7, type: 'rock', emoji: '🪨', jackpot: 0.02, good: 0.10, small: 0.20, empty: 0.68, duration: 60, boost_percentage: 100 },
    { value: 8, type: 'gold', emoji: '🥇', jackpot: 0.05, good: 0.15, small: 0.20, empty: 0.60, duration: 90, boost_percentage: 150 },
    { value: 9, type: 'ice', emoji: '❄️', jackpot: 0.03, good: 0.12, small: 0.20, empty: 0.65, duration: 90, boost_percentage: 100 }
  ];

  const asteroidsConfig = config && config.length > 0 ? config : defaultConfig;

  // Стили для типов астероидов
  const typeStyles: Record<string, { bg: string; border: string; label: string }> = {
    rock: { bg: 'rgba(156, 163, 175, 0.2)', border: '#9ca3af', label: `🪨 ${t('games.asteroidMiner.types.rock')}` },
    ice: { bg: 'rgba(59, 130, 246, 0.2)', border: '#3b82f6', label: `❄️ ${t('games.asteroidMiner.types.ice')}` },
    gold: { bg: 'rgba(245, 158, 11, 0.2)', border: '#f59e0b', label: `🥇 ${t('games.asteroidMiner.types.gold')}` },
    rare: { bg: 'rgba(168, 85, 247, 0.3)', border: '#a855f7', label: `💎 ${t('games.asteroidMiner.types.rare')}` }
  };

  const getAsteroidConfig = (id: number) => asteroidsConfig.find(a => a.value === id) || asteroidsConfig[0];
  const selectedAsteroidConfig = selectedAsteroid ? getAsteroidConfig(selectedAsteroid) : null;

  // Вычисляем средние значения
  const avgEmptyChance = Math.round(asteroidsConfig.reduce((sum, c) => sum + c.empty, 0) / asteroidsConfig.length * 100);
  const totalWinChance = 100 - avgEmptyChance;

  const handlePlay = useCallback(() => {
    if (selectedAsteroid !== null) {
      console.log('🎮 AsteroidMiner play!', selectedAsteroid);
      onPlay(selectedAsteroid);
    }
  }, [selectedAsteroid, onPlay]);

  const playBtnEvents = useTouchClick(handlePlay, selectedAsteroid === null || isPlaying);

  return (
    <div className="game-modal">
      <div className="game-content asteroid-miner">
        <div className="game-header">
          <h2>⛏️ {t('games.asteroidMiner.title')}</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="help-btn"
              onClick={() => setShowHelp(true)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                color: 'white'
              }}
            >
              ❓
            </button>
            <button className="close-btn" onClick={onClose}>✖</button>
          </div>
        </div>

        {/* Индикатор активного буста */}
        {activeBoost && activeBoost.timeLeft > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '6px',
            padding: '4px 6px',
            margin: '3px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
          }}>
            <span style={{ fontSize: '12px' }}>⚡</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', color: 'white', fontSize: '9px' }}>
                {t('games.activeBoost')} x{activeBoost.multiplier}
              </div>
              <div style={{ fontSize: '8px', color: 'rgba(255, 255, 255, 0.8)' }}>
                {t('games.remaining')}: {Math.floor(activeBoost.timeLeft / 60)}:{(activeBoost.timeLeft % 60).toString().padStart(2, '0')}
              </div>
            </div>
          </div>
        )}

        {/* Отображение ошибки */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '6px',
            padding: '4px',
            margin: '3px 0',
            color: '#ef4444',
            textAlign: 'center',
            fontSize: '9px'
          }}>
            ❌ {error}
          </div>
        )}

        {/* Легенда типов */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '6px',
          fontSize: '9px'
        }}>
          <span style={{ color: '#9ca3af' }}>🪨 {t('games.asteroidMiner.types.rock')}</span>
          <span style={{ color: '#3b82f6' }}>❄️ {t('games.asteroidMiner.types.ice')}</span>
          <span style={{ color: '#f59e0b' }}>🥇 {t('games.asteroidMiner.types.gold')}</span>
          <span style={{ color: '#a855f7' }}>💎 {t('games.asteroidMiner.types.rare')}</span>
        </div>

        <div className="asteroids-field">
          <p style={{ fontSize: '11px', marginBottom: '8px' }}>{t('games.asteroidMiner.chooseAsteroid')}</p>
          <div className="asteroids-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
            {asteroidsConfig.map((asteroid) => {
              const style = typeStyles[asteroid.type] || typeStyles.rock;
              const winChance = Math.round((1 - asteroid.empty) * 100);
              return (
                <button
                  key={asteroid.value}
                  className={`asteroid-btn ${selectedAsteroid === asteroid.value ? 'selected' : ''} ${isPlaying ? 'mining' : ''}`}
                  onClick={() => setSelectedAsteroid(asteroid.value)}
                  disabled={isPlaying}
                  style={{
                    background: selectedAsteroid === asteroid.value ? style.border : style.bg,
                    borderColor: style.border,
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    padding: '8px 4px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px',
                    borderRadius: '8px',
                    cursor: isPlaying ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{asteroid.emoji || '🪨'}</span>
                  <span style={{ fontSize: '8px', opacity: 0.9 }}>{winChance}% {t('games.win')}</span>
                  <span style={{ fontSize: '7px', opacity: 0.7 }}>+{asteroid.boost_percentage}% • {asteroid.duration}{t('games.sec')}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Информация о выбранном астероиде */}
        {selectedAsteroidConfig && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            padding: '8px',
            margin: '8px 0',
            textAlign: 'center',
            fontSize: '10px'
          }}>
            <strong>{typeStyles[selectedAsteroidConfig.type]?.label || selectedAsteroidConfig.type}</strong>:
            {' '}{Math.round((1 - selectedAsteroidConfig.empty) * 100)}% {t('games.chance')} → +{selectedAsteroidConfig.boost_percentage}% {t('games.boost')} {t('games.forSec', { sec: selectedAsteroidConfig.duration })}
          </div>
        )}

        <button
          className="play-btn mobile-touch-btn"
          onTouchEnd={playBtnEvents.onTouchEnd}
          onClick={playBtnEvents.onClick}
          disabled={selectedAsteroid === null || isPlaying}
          style={{ touchAction: 'manipulation' }}
        >
          {isPlaying ? `⛏️ ${t('games.asteroidMiner.actions.mining')}` : (error ? `🔄 ${t('games.asteroidMiner.actions.tryAgain')}` : `⛏️ ${t('games.asteroidMiner.actions.startMining')}`)}
        </button>

        {/* Модалка с инструкцией */}
        {showHelp && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              padding: '20px'
            }}
            onClick={() => setShowHelp(false)}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #1e1e2e 0%, #2a2a3e 100%)',
                borderRadius: '12px',
                padding: '16px',
                maxWidth: '340px',
                width: '100%',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>📖 {t('games.help.howToPlay')}</h3>
                <button
                  onClick={() => setShowHelp(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  ✖
                </button>
              </div>

              <div style={{ fontSize: '12px', lineHeight: '1.5', color: 'rgba(255, 255, 255, 0.9)' }}>
                <p style={{ marginBottom: '10px' }}>
                  <strong>🎯 {t('games.help.goal')}:</strong><br />
                  {t('games.asteroidMiner.help.description')}
                </p>

                <div style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
                  <p style={{ marginBottom: '6px', fontWeight: 'bold' }}>🪨 {t('games.asteroidMiner.help.asteroidTypes')}:</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#9ca3af' }}>🪨 <strong>{t('games.asteroidMiner.types.rock')}</strong>: 32% {t('games.win')}, +100%, 60{t('games.sec')}</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#3b82f6' }}>❄️ <strong>{t('games.asteroidMiner.types.ice')}</strong>: 35% {t('games.win')}, +100%, 90{t('games.sec')}</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#f59e0b' }}>🥇 <strong>{t('games.asteroidMiner.types.gold')}</strong>: 40% {t('games.win')}, +150%, 90{t('games.sec')}</p>
                  <p style={{ margin: '4px 0', fontSize: '10px', color: '#a855f7' }}>💎 <strong>{t('games.asteroidMiner.types.rare')}</strong>: 48% {t('games.win')}, +200%, 120{t('games.sec')}</p>
                </div>

                <p style={{ marginBottom: '10px' }}>
                  <strong>💡 {t('games.help.strategy')}:</strong><br />
                  {t('games.asteroidMiner.help.strategy1')}<br />
                  {t('games.asteroidMiner.help.strategy2')}
                </p>

                <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  🎮 {t('games.cost')}: <strong>{t('games.free')}</strong><br />
                  🎯 {t('games.attemptsPerDay', { count: 3 })}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AsteroidMiner;